import React, { useState } from 'react';
import { Clock, Plus } from 'lucide-react';
import type { EditorChatThread } from '../../lib/editorChatHistory';

type Props = {
  entries: EditorChatThread[];
  activeId: string | null;
  onSelect: (entry: EditorChatThread) => void;
  onNew: () => void;
};

function formatRelativePt(ts: number): string {
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (s < 45) return 'agora';
  if (s < 3600) return `há ${Math.max(1, Math.round(s / 60))} min`;
  if (s < 86400) return `há ${Math.max(1, Math.round(s / 3600))} h`;
  const d = Math.round(s / 86400);
  return d <= 1 ? 'ontem' : `há ${d} dias`;
}

function thumbsOf(entry: EditorChatThread): Array<{ src: string; kind: 'image' | 'video' }> {
  const thumbs: Array<{ src: string; kind: 'image' | 'video' }> = [];
  const add = (src?: string, kind: 'image' | 'video' = 'image') => {
    if (!src || !/^https?:\/\//i.test(src)) return;
    if (thumbs.some((t) => t.src === src)) return;
    thumbs.push({ src, kind });
  };
  for (const clip of entry.timelineClips || []) {
    add(clip.thumbnailUrl || clip.sourceUrl, /image/i.test(clip.type) ? 'image' : 'video');
    if (thumbs.length >= 3) return thumbs;
  }
  for (const msg of entry.messages) {
    for (const img of msg.images || []) {
      add(img.preview || img.url, img.kind === 'video' ? 'video' : 'image');
      if (thumbs.length >= 3) return thumbs;
    }
  }
  return thumbs;
}

export default function EditorChatHistoryRail({ entries, activeId, onSelect, onNew }: Props) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const hovered = hoveredId ? entries.find((e) => e.id === hoveredId) : null;
  const isBlank = !activeId;

  return (
    <aside className="staging-history-rail" aria-label="Histórico do chat do editor">
      <button
        type="button"
        className={`staging-history-rail__new${isBlank ? ' is-active' : ''}`}
        title="Novo chat"
        aria-label="Novo chat"
        onClick={onNew}
      >
        <Plus size={13} strokeWidth={2.5} />
      </button>
      {entries.length > 0 && (
        <>
          <div className="staging-history-rail__icon" title="Histórico — clique para voltar a um chat">
            <Clock size={13} />
          </div>
          <div className="staging-history-rail__track">
            {entries.map((entry, idx) => {
              const active = entry.id === activeId;
              const hoveredTick = entry.id === hoveredId;
              const thumbs = thumbsOf(entry);
              const userTurns = entry.messages.filter((m) => m.role === 'user').length;
              return (
                <button
                  key={entry.id}
                  type="button"
                  className={`staging-history-rail__tick${active ? ' is-active' : ''}${hoveredTick ? ' is-hover' : ''}`}
                  aria-label={`Chat ${formatRelativePt(entry.updatedAt)}: ${entry.title.slice(0, 80)}`}
                  title={entry.title.slice(0, 120)}
                  onMouseEnter={() => setHoveredId(entry.id)}
                  onMouseLeave={() => setHoveredId((cur) => (cur === entry.id ? null : cur))}
                  onFocus={() => setHoveredId(entry.id)}
                  onBlur={() => setHoveredId((cur) => (cur === entry.id ? null : cur))}
                  onClick={() => onSelect(entry)}
                >
                  <span className="staging-history-rail__line" />
                  {hoveredTick && hovered && (
                    <span className="staging-history-rail__tip" role="tooltip">
                      {thumbs.length > 0 && (
                        <span className="staging-history-rail__thumbs">
                          {thumbs.map((thumb) => (
                            thumb.kind === 'video' ? (
                              <video key={thumb.src} src={thumb.src} muted preload="metadata" />
                            ) : (
                              <img key={thumb.src} src={thumb.src} alt="" />
                            )
                          ))}
                        </span>
                      )}
                      <span className="staging-history-rail__meta">
                        {formatRelativePt(hovered.updatedAt)}
                        {idx === 0 ? ' · atual' : ''}
                      </span>
                      <span className="staging-history-rail__prompt">{hovered.title}</span>
                      <span className="staging-history-rail__meta">
                        {userTurns} pedido{userTurns === 1 ? '' : 's'}
                        {(hovered.timelineClips?.length || 0) > 0
                          ? ` · ${hovered.timelineClips?.length} clip${hovered.timelineClips?.length === 1 ? '' : 's'}`
                          : ''}
                      </span>
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </aside>
  );
}
